import {Wizard} from "../../../../models/wizard/wizard";
import {
    BookingFlowDepartingFlightSelectionStep
} from "../../steps/booking-flow/booking-flow-departing-flight-selection.step";
import {BookingFlowReturnFlightSelectionStep} from "../../steps/booking-flow/booking-flow-return-flight-selection.step";
import {BookingFlowPassengerDetailsStep} from "../../steps/booking-flow/booking-flow-passenger-details.step";
import {BookingFlowSeatsSelectionStep} from "../../steps/booking-flow/booking-flow-seats-selection.step";
import {BookingFlowBagsSelectionStep} from "../../steps/booking-flow/booking-flow-bags-selection.step";
import {BookingFlowExtrasSelectionStep} from "../../steps/booking-flow/booking-flow-extras-selection.step";
import {BookingFlowPaymentStep} from "../../steps/booking-flow/booking-flow-payment.step";
import {BookingStrategyBase, IBookingStrategyOptions} from "../booking-strategy-base";
import {BookingFlowFinalPriceStep} from "../../steps/booking-flow/booking-flow-final-price.step";
import {ANALYTICS_AFFILIATIONS} from "../analytics/analytics-affiliations";
import {IBookingStrategyAnalyticsConfiguration} from "../analytics/booking-strategy-analytics-configuration.interface";
import {BookingFlowTypeEnum} from "../booking-flow-type.enum";
import {IFareToSell} from "../../models/booking-view-model.interface";
import {
    IDotRezPartialBookingSessionData
} from "../../../dot-rez-api/data-contracts/booking/dot-rez-booking-session-data.interface";
import {
    SellDepartureJourneyMutation
} from "../../models/mutation-actions/sell-trip/booking-flow/sell-departure-journey.mutation";
import {
    SellReturnJourneyMutation
} from "../../models/mutation-actions/sell-trip/booking-flow/sell-return-journey.mutation";
import {ISsrType} from "../../../ssr-types/ssr-types.service.interface";
import {
    SellDepartureJourneyBundleMutation
} from "../../models/mutation-actions/sell-bundle/sell-departure-journey-bundle.mutation";
import {
    SellReturnJourneyBundleMutation
} from "../../models/mutation-actions/sell-bundle/sell-return-journey-bundle.mutation";
import {NullableString} from "../../../../types/nullable-types";
import {ShoppingCartModeEnum} from "../booking-strategy.interface";
import {IServiceFactory} from "../../../service-factory.interface";
import {IRoute} from "../../../navigation/models/route.interface";
import {BookingFlowEveryMundoAnalyticsReporter} from "./booking-flow-every-mundo-analytics-reporter";
import {IStorage} from "../../../storage/storage.interface";
import {BookingSessionStorageKeys} from "../../models/storage/booking-session-storage-keys";
import {ISellJourneyBundleOptions} from "../booking-strategy-to-booking.adapter.interface";


export class BookingFlowStrategy extends BookingStrategyBase {

    constructor(services: IServiceFactory, options: IBookingStrategyOptions) {
        super(services, options);

        this._everyMundoReporter = new BookingFlowEveryMundoAnalyticsReporter(this);
    }

    private readonly _everyMundoReporter: BookingFlowEveryMundoAnalyticsReporter

    async dispose(): Promise<void> {
        this._everyMundoReporter.dispose();
        await super.dispose();
    }

    get hideFinalPriceStep(): boolean {
        return true;
    }

    get allowHold(): boolean {
        return this.services.user.profile.allowHoldBooking;
    }

    get bookingFlowType(): BookingFlowTypeEnum {
        return BookingFlowTypeEnum.Booking;
    }

    get canBeModified(): boolean {
        return true;
    }


    canShowSsr(ssrType: ISsrType): boolean {
        return ssrType.canBeShownOnBookingFlow(this.booking);
    }

    get allowedShoppingCartModes(): ShoppingCartModeEnum[] {
        return [ShoppingCartModeEnum.ShowAllPurchases];
    }

    protected _createSteps(sessionStorage: IStorage<BookingSessionStorageKeys>): Wizard {
        const wizard = new Wizard(this.services, 'bookingFlowStrategy', sessionStorage);
        wizard.addStep(new BookingFlowDepartingFlightSelectionStep(this.booking, wizard))
        wizard.addStep(new BookingFlowReturnFlightSelectionStep(this.booking, wizard))
        wizard.addStep(new BookingFlowPassengerDetailsStep(this.booking, wizard));
        wizard.addStep(new BookingFlowSeatsSelectionStep(this.booking, wizard));
        wizard.addStep(new BookingFlowBagsSelectionStep(this.booking, wizard));
        wizard.addStep(new BookingFlowExtrasSelectionStep(this.booking, wizard));
        wizard.addStep(new BookingFlowFinalPriceStep(this.booking, wizard));
        wizard.addStep(new BookingFlowPaymentStep(this.booking, wizard));
        return wizard;
    }


    get allowPromoCode(): boolean {
        return true;
    }

    get analyticsConfiguration(): IBookingStrategyAnalyticsConfiguration {
        return {
            affiliation: ANALYTICS_AFFILIATIONS.booking,
            shouldReportFares: true
        }
    }

    async sellDepartureJourney(fareToSell: IFareToSell, onAfterSell: (bookingSessionData: IDotRezPartialBookingSessionData) => Promise<void>): Promise<void> {
        if (this.booking.departureJourney?.wasSoldFromThisFare(fareToSell)) {
            return;
        }

        const mutation = new SellDepartureJourneyMutation(
            this.booking,
            fareToSell,
            async (bookingSessionData) => {
                await onAfterSell(bookingSessionData);
            });

        this.booking.mutationsManager.startSellDepartureJourneyMutation(mutation);

        await mutation.waitForMutation();
    }

    async sellDepartureJourneyBundle(bundleCode: string, options?: ISellJourneyBundleOptions): Promise<void> {
        const departureJourney = this.booking.departureJourney;
        if (!departureJourney) {
            throw new Error('You cannot sell departure journey bundle if there is no departure journey');
        }

        if (bundleCode === departureJourney.selectedBundle?.bundleCode && !options?.ssrsUpgradePairs) {
            return;
        }

        departureJourney.markBundleAsSelected(bundleCode); //we need to do this in order for the UI to update and not waiting for the request to finish
        this.booking.mutationsManager.startMutation(new SellDepartureJourneyBundleMutation(this.booking, bundleCode, options?.ssrsUpgradePairs ?? []));
        this.booking.mutationsManager.startRetrieveSsrsAvailability();
        this.booking.mutationsManager.startRetrieveSeatsMap();
    }

    async sellReturnJourney(fareToSell: IFareToSell, onAfterSell: (bookingSessionData: IDotRezPartialBookingSessionData) => Promise<void>): Promise<void> {
        if (this.booking.returnJourney?.wasSoldFromThisFare(fareToSell)) {
            return;
        }

        const mutation = new SellReturnJourneyMutation(this.booking,
            fareToSell,
            async (bookingSessionData) => {
                await onAfterSell(bookingSessionData);
            });

        this.booking.mutationsManager.startSellReturnJourneyMutation(mutation);

        await mutation.waitForMutation();
    }

    async sellReturnJourneyBundle(bundleCode: string): Promise<void> {

        if(!this.booking.departureJourney?.selectedBundle) {
            throw new Error('You cannot sell return journey bundle if there is no departure journey bundle selected');
        }

        const returnJourney = this.booking.returnJourney;

        if (!returnJourney) {
            throw new Error('You cannot sell return journey bundle if there is no return journey');
        }

        /*
        if (bundleCode === returnJourney.selectedBundle?.bundleCode && !options?.ssrsUpgradePairs) {
            return;
        }
         */

        returnJourney.markBundleAsSelected(bundleCode); //we need to do this in order for the UI to update and not waiting for the request to finish

        this.booking.mutationsManager.startMutation(new SellReturnJourneyBundleMutation(this.booking, bundleCode));

        this.booking.mutationsManager.startRetrieveSsrsAvailability();
        this.booking.mutationsManager.startRetrieveSeatsMap();
    }

    get departureBundleNameToShowOnFare(): NullableString {
        // on booking flow we don't want to show the bundle name above the fare because we have a dedicated step for bundle selection
        return null;
    }

    get returnBundleNameToShowOnFare(): NullableString {
        // on booking flow we don't want to show the bundle name above the fare because we have a dedicated step for bundle selection
        return null;
    }

    get allowSellInsurance(): boolean {
        return this.services.configuration.insurance.isEnabled;
    }

    get shouldSyncContactWithPrimaryPassenger(): boolean {
        return true;
    }

    getFinalPriceRoute(): IRoute {
        return this.services.navigator.routes.booking.finalPrice;
    }
    getPaymentRoute(): IRoute {
        return this.services.navigator.routes.booking.payment;
    }

    getFinalizePaymentRoute(): IRoute {
        return this.services.navigator.routes.booking.finalizePayment;
    }


    async onPaymentSuccess(): Promise<void> {
        await this.services.booking.switchToManageMyBooking();
    }

    async goBackToFlightSelection(): Promise<void> {
        await this.steps.goToStepByRoute(this.services.navigator.routes.booking.departingFlight);
    }

    get allowChangeSearch(): boolean {
        return true;
    }

    get shouldRenderBagsInExtras(): boolean {
        return false;
    }

    get shouldValidateBags(): boolean {
        return true;
    }
}
